import { ApolloClient, type DocumentNode } from "@apollo/client";
import { MasterDataGraphQlAdapter } from "@src/infrastructure/adapters/graphQl/MasterDataGraphQlAdapter";
import {
  CREATE_MASTER_DATA_RETAILER_MATCH_RULE,
  GET_MASTER_DATA_RETAILER_MATCH_RULES,
  GET_MASTER_DATA_RETAILER_MATCH_SOURCE_COLUMNS,
  UPDATE_MASTER_DATA_RETAILER_MATCH_RULE,
} from "@src/domain/graphql/masterData";
import {
  MasterDataRetailerMatchRule,
  type IMasterDataRetailerMatchRule,
} from "@src/domain/entities/masterData";
import type {
  CreateMasterDataRetailerMatchRuleDTO,
  GetMasterDataRetailerMatchRulesDTO,
  UpdateMasterDataRetailerMatchRuleDTO,
} from "@src/domain/dto/masterData";
import type {
  IMasterDataRetailerMatchRuleRepository,
  MasterDataRetailerMatchRulesListResult,
} from "@src/domain/repositories/masterData";
import {
  requireMasterDataMutationData,
  throwIfMasterDataGraphQLError,
  throwIfMasterDataMutationError,
  toMasterDataListResult,
} from "./masterDataRepository.shared";

function mapMatchRuleItem(
  item: IMasterDataRetailerMatchRule,
): MasterDataRetailerMatchRule {
  return MasterDataRetailerMatchRule.from({
    ruleId: item.ruleId,
    retailerId: item.retailerId,
    matchPattern: item.matchPattern,
    matchPriority: item.matchPriority,
    sourceColumn: item.sourceColumn,
    retailerName: item.retailerName ?? null,
  });
}

export class MasterDataRetailerMatchRuleRepository
  implements IMasterDataRetailerMatchRuleRepository
{
  private readonly client: ApolloClient;

  constructor(
    client: ApolloClient = MasterDataGraphQlAdapter.getInstance().getClient(),
  ) {
    this.client = client;
  }

  async getMatchRules(
    params: GetMasterDataRetailerMatchRulesDTO,
  ): Promise<MasterDataRetailerMatchRulesListResult> {
    const { data, error } = await this.client.query<{
      masterDataRetailerMatchRules: {
        items: IMasterDataRetailerMatchRule[];
        total: number;
        page: number;
        limit: number;
      };
    }>({
      query: GET_MASTER_DATA_RETAILER_MATCH_RULES,
      variables: {
        params: {
          search: params.search ?? null,
          retailerId: params.retailerId ?? null,
          page_number: params.page_number,
          page_size: params.page_size,
        },
      },
      fetchPolicy: "network-only",
      errorPolicy: "all",
    });

    throwIfMasterDataGraphQLError({ error });

    const response = data?.masterDataRetailerMatchRules;
    if (!response) {
      throw new Error("Failed to fetch master data retailer match rules");
    }

    return toMasterDataListResult(
      response,
      mapMatchRuleItem,
      params.page_number,
      params.page_size,
    );
  }

  async getMatchSourceColumns(): Promise<string[]> {
    const { data, error } = await this.client.query<{
      masterDataRetailerMatchSourceColumns: {
        items: { sourceColumn: string }[];
      };
    }>({
      query: GET_MASTER_DATA_RETAILER_MATCH_SOURCE_COLUMNS,
      fetchPolicy: "network-only",
      errorPolicy: "all",
    });

    throwIfMasterDataGraphQLError({ error });

    const response = data?.masterDataRetailerMatchSourceColumns;
    if (!response) {
      throw new Error("Failed to fetch match source columns");
    }

    return (response.items ?? [])
      .map((item) => item.sourceColumn?.trim() ?? "")
      .filter((sourceColumn) => sourceColumn !== "");
  }

  async createMatchRule(
    params: CreateMasterDataRetailerMatchRuleDTO,
  ): Promise<MasterDataRetailerMatchRule> {
    return this.putMatchRule(
      CREATE_MASTER_DATA_RETAILER_MATCH_RULE,
      {
        retailerId: params.retailerId,
        matchPattern: params.matchPattern,
        matchPriority: params.matchPriority,
        sourceColumn: params.sourceColumn,
      },
      (data) => data?.createMasterDataRetailerMatchRule,
      "Failed to create match rule",
    );
  }

  async updateMatchRule(
    params: UpdateMasterDataRetailerMatchRuleDTO,
  ): Promise<MasterDataRetailerMatchRule> {
    return this.putMatchRule(
      UPDATE_MASTER_DATA_RETAILER_MATCH_RULE,
      {
        ruleId: params.ruleId,
        matchPattern: params.matchPattern,
        matchPriority: params.matchPriority,
        sourceColumn: params.sourceColumn,
      },
      (data) => data?.updateMasterDataRetailerMatchRule,
      "Failed to update match rule",
    );
  }

  private async putMatchRule(
    mutation: DocumentNode,
    mutationParams: Record<string, unknown>,
    pickResponse: (
      data:
        | {
            createMasterDataRetailerMatchRule?: IMasterDataRetailerMatchRule;
            updateMasterDataRetailerMatchRule?: IMasterDataRetailerMatchRule;
          }
        | null
        | undefined,
    ) => IMasterDataRetailerMatchRule | null | undefined,
    fallbackMessage: string,
  ): Promise<MasterDataRetailerMatchRule> {
    const result = await this.client.mutate<{
      createMasterDataRetailerMatchRule?: IMasterDataRetailerMatchRule;
      updateMasterDataRetailerMatchRule?: IMasterDataRetailerMatchRule;
    }>({
      mutation,
      variables: { params: mutationParams },
      errorPolicy: "all",
    });

    throwIfMasterDataMutationError(result.error);

    return mapMatchRuleItem(
      requireMasterDataMutationData(
        pickResponse(result.data),
        result.error,
        fallbackMessage,
      ),
    );
  }
}
